import * as LiveKitComponents from '@livekit/components-react';
import {
  RoomEvent,
  Track,
  type RemoteTrackPublication,
  type Room,
} from 'livekit-client';
import React from 'react';

type RoomWithEvents = Pick<Room, 'remoteParticipants' | 'on' | 'off'>;

const useMaybeRoomContext = LiveKitComponents.useMaybeRoomContext
  ?? (() => undefined);

function canManageRemoteAudio(room: RoomWithEvents | undefined): room is RoomWithEvents {
  if (!room?.remoteParticipants) return false;
  return typeof room.on === 'function' && typeof room.off === 'function';
}

function forEachSubscribedRemoteAudioPublication(
  room: RoomWithEvents,
  apply: (publication: RemoteTrackPublication) => void,
): void {
  room.remoteParticipants.forEach((participant) => {
    participant.trackPublications.forEach((publication) => {
      if (publication.kind !== Track.Kind.Audio) return;
      if (!publication.isSubscribed) return;
      apply(publication as RemoteTrackPublication);
    });
  });
}

function setRemoteAudioEnabled(room: RoomWithEvents, enabled: boolean): void {
  forEachSubscribedRemoteAudioPublication(room, (publication) => {
    publication.setEnabled(enabled);
  });
}

/**
 * Wall tile audio gate: remote voice bytes flow only while Observe is active.
 * Uses LiveKit setEnabled (not unsubscribe) so resume stays fast when the
 * speaker mounts.
 */
export function useRemoteAudioPauseUntilObserve(isObserving: boolean): void {
  const room = useMaybeRoomContext();

  React.useEffect(() => {
    if (!canManageRemoteAudio(room)) return undefined;
    setRemoteAudioEnabled(room, isObserving);
    return undefined;
  }, [room, isObserving]);

  React.useEffect(() => {
    if (!canManageRemoteAudio(room)) return undefined;
    if (isObserving) return undefined;

    const pauseLateAudio = (
      _track: unknown,
      publication: RemoteTrackPublication,
    ) => {
      if (publication.kind !== Track.Kind.Audio) return;
      if (!publication.isSubscribed) return;
      publication.setEnabled(false);
    };

    room.on(RoomEvent.TrackSubscribed, pauseLateAudio);
    return () => {
      room.off(RoomEvent.TrackSubscribed, pauseLateAudio);
    };
  }, [room, isObserving]);
}
